interface ObjectWithEmail {
  email?: string | null;
}
interface ObjectWithORCiD {
  orcid?: string | null;
}

interface ResultObject {
  // A set of all duplicate emails
  duplicateEmails: Set<string>;
  // The number of unique duplicate emails
  duplicateCount: number;
  // The number of authors who have duplicate emails
  authorsWithDuplicateEmailsCount: number;
  // A map of email to the number of times it appears in the list
  emailCounts: Record<string, number>;
}
interface ResultObjectORCiD {
  // A set of all duplicate emails
  duplicateORCiDs: Set<string>;
  // The number of unique duplicate emails
  duplicateCount: number;
  // The number of authors who have duplicate emails
  authorsWithDuplicateORCiDCount: number;
  // A map of email to the number of times it appears in the list
  orcidCounts: Record<string, number>;
}

export function useDuplicateEmails<T extends ObjectWithEmail>(
  users: T[]
): ResultObject {
  const emailCounts: Record<string, number> = {};
  users.forEach(user => {
    if (user.email) {
      const email = user.email.toLowerCase().trim();
      emailCounts[email] = (emailCounts[email] || 0) + 1;
    }
  });
  const duplicateEmails = new Set(
    Object.entries(emailCounts)
      .filter(([, count]) => count > 1)
      .map(([email]) => email)
  );

  // Calculate the number of authors who have duplicate emails
  const authorsWithDuplicateEmailsCount = users.filter(
    user => user.email && duplicateEmails.has(user.email.toLowerCase().trim())
  ).length;

  return {
    duplicateEmails,
    duplicateCount: duplicateEmails.size,
    authorsWithDuplicateEmailsCount,
    emailCounts
  };
}

export function useDuplicateORCiD<T extends ObjectWithORCiD>(
  users: T[]
): ResultObjectORCiD {
  const orcidCounts: Record<string, number> = {};
  users.forEach(user => {
    if (user.orcid) {
      const orcid = user.orcid.toLowerCase().trim();
      orcidCounts[orcid] = (orcidCounts[orcid] || 0) + 1;
    }
  });
  const duplicateORCiDs = new Set(
    Object.entries(orcidCounts)
      .filter(([, count]) => count > 1)
      .map(([orcid]) => orcid)
  );

  // Calculate the number of authors who have duplicate emails
  const authorsWithDuplicateORCiDCount = users.filter(
    user => user.orcid && duplicateORCiDs.has(user.orcid.toLowerCase().trim())
  ).length;

  return {
    duplicateORCiDs,
    duplicateCount: duplicateORCiDs.size,
    authorsWithDuplicateORCiDCount,
    orcidCounts
  };
}
